import { useMemo, useState, useRef, useEffect } from "react";
import { Input } from "@racoon/ui";
import { Search, X } from "lucide-react";
import { useT, Namespace } from "@racoon/i18n";
import { MapObject } from "./map-view";
import { IcCar, IcLocation } from "@racoon/icons";
import { humanTruckType } from "~/pages/dashboard/autopark/helpers";
import { TruckType } from "@racoon/api";
import { isHouseholdObject } from "../model/constants/icons";

import { normalizeForSearch } from "@racoon/lib";
interface MapSearchProps {
  objects: MapObject[];
  trucks: MapObject[];
  onSelect: (item: MapObject) => void;
  /** First focus: lets the caller fetch a heavy list only once someone searches. */
  onActivate?: () => void;
}

export const MapSearch = ({ objects, trucks, onSelect, onActivate }: MapSearchProps) => {
  const t = useT();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return [];

    const lowerTerm = normalizeForSearch(searchTerm);
    const result: MapObject[] = [];

    // Filter trucks
    trucks.forEach((truck) => {
      if (
        normalizeForSearch(truck.name).includes(lowerTerm) ||
        String(truck.id).includes(lowerTerm)
      ) {
        result.push(truck);
      }
    });

    // Filter objects
    objects.forEach((obj) => {
      if (
        normalizeForSearch(obj.name).includes(lowerTerm) ||
        String(obj.id).includes(lowerTerm) ||
        String(obj.localIndex).includes(lowerTerm)
      ) {
        result.push(obj);
      }
    });

    return result.slice(0, 50); // Limit results
  }, [objects, trucks, searchTerm]);

  const handleSelect = (item: MapObject) => {
    onSelect(item);
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={wrapperRef} className="relative w-64">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500 z-10" />
        <Input
          placeholder={t(Namespace.Common, "search") || "Search..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => {
            setOpen(true);
            onActivate?.();
          }}
          size="sm"
          className="pl-9 pr-8 bg-white shadow-md"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm("")}
            className="absolute right-2.5 top-2.5 text-gray-500 hover:text-gray-700 z-10"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-md shadow-lg border max-h-[300px] overflow-auto z-50">
          {filteredItems.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              {searchTerm
                ? t(Namespace.Common, "noResults" as any) || "No results found"
                : t(Namespace.Common, "startTyping" as any) || "Start typing to search"}
            </div>
          ) : (
            <div className="p-1 space-y-1">
              {filteredItems.map((item) => (
                <button
                  key={`${item.type}-${item.id}`}
                  onClick={() => handleSelect(item)}
                  className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 flex items-center gap-3 transition-colors"
                >
                  <div className="flex-shrink-0 text-brand-blue">
                    {item.type === "truck" ? (
                      item.truckType === TruckType.TRUCK_TYPE_CONSTRUCTION ? (
                        <IcCar className="h-5 w-5 text-brand-blue" />
                      ) : item.truckType === TruckType.TRUCK_TYPE_MSW ||
                        item.truckType === TruckType.TRUCK_TYPE_DOUBLE_CONTAINER ||
                        item.truckType === TruckType.TRUCK_TYPE_TIPPER ? (
                        <IcCar className="h-5 w-5 text-brand-green" />
                      ) : (
                        <IcCar className="h-5 w-5" />
                      )
                    ) : isHouseholdObject(item) ? (
                      <IcLocation className="h-5 w-5 text-brand-green" />
                    ) : (
                      <IcLocation className="h-5 w-5 text-brand-blue" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{item.name || item.id}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {item.type === "truck"
                        ? humanTruckType(item.truckType, t)
                        : item.companyName || "Object"}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
